import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { isAllRentalsSlug, isInterimAllRentalsSlug } from '@/src/lib/seo/allRentalsSlug';
import { getPropertyTypeLabelKey, type PropertyTypeLabelKey } from '@/src/lib/utils/listingPropertyType';

const DEFAULT_HDP_BACK_HREF = '/';
const ROOMS_SRP_SLUG = 'rooms-for-rent';

const SRP_SLUG_FOR_PROPERTY_TYPE: Partial<Record<PropertyTypeLabelKey, string>> = {
  apartment: 'apartments-for-rent',
  condo: 'condos-for-rent',
  house: 'houses-for-rent',
  townhouse: 'townhomes-for-rent',
};

type AreaLike = {
  resourceId?: string | null;
  type?: string | null;
  uriV2?: string | null;
};

type ListingBackHrefSource = Partial<Pick<PropertyDetailListing, 'address' | 'listingType' | 'propertyType'>> & {
  areas?: {
    breadcrumbs?: Array<AreaLike> | null;
    specific?: AreaLike | null;
  } | null;
};

function trimSlashes(value: string) {
  return value.replace(/^\/+|\/+$/g, '');
}

function srpHrefFromResourceId(resourceId: string | null | undefined): string | null {
  const normalized = resourceId?.trim();
  if (!normalized) return null;
  return `/${trimSlashes(normalized)}`;
}

function srpHrefFromArea(area: AreaLike | null | undefined): string | null {
  const hrefFromResourceId = srpHrefFromResourceId(area?.resourceId);
  if (hrefFromResourceId) return hrefFromResourceId;

  const uri = area?.uriV2?.trim();
  if (!uri?.startsWith('/')) return null;
  const [uriResourceId, uriSlug, ...rest] = uri.split('/').filter(Boolean);
  if (!uriResourceId) return null;
  if (rest.length === 0 && (!uriSlug || isAllRentalsSlug(uriSlug) || isInterimAllRentalsSlug(uriSlug))) {
    return `/${uriResourceId}`;
  }
  return uri.replace(/\/+$/g, '');
}

function findCityArea(areas: ListingBackHrefSource['areas']): AreaLike | null {
  const cityBreadcrumb = areas?.breadcrumbs?.find((breadcrumb) => breadcrumb?.type?.toLowerCase() === 'city');
  if (cityBreadcrumb) return cityBreadcrumb;

  return areas?.specific?.type?.toLowerCase() === 'city' ? areas.specific : null;
}

function slugifyResourcePart(value: string | null | undefined): string | null {
  const slug = value
    ?.trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug || null;
}

function cityResourceIdFromAddress(address: ListingBackHrefSource['address']): string | null {
  const city = slugifyResourcePart(address?.city);
  const state = slugifyResourcePart(address?.state);
  return city && state ? `${city}-${state}` : null;
}

function srpSlugForListing(listing: ListingBackHrefSource | null | undefined): string | undefined {
  if (listing?.listingType?.toLowerCase() === 'room') return ROOMS_SRP_SLUG;
  return SRP_SLUG_FOR_PROPERTY_TYPE[getPropertyTypeLabelKey(listing?.propertyType)];
}

function withListingSrpSlug(areaHref: string, listing: ListingBackHrefSource | null | undefined): string {
  const slug = srpSlugForListing(listing);
  const isBareArea = trimSlashes(areaHref).split('/').length === 1;
  return slug && isBareArea ? `${areaHref}/${slug}` : areaHref;
}

/**
 * HDP back links need a deterministic destination because direct SSR visits
 * and shared listing URLs have no SRP entry in browser history. Prefer the
 * city breadcrumb so neighborhood/building listings still return to the
 * broader city SRP, filtered to the listing's property type when it has one.
 */
export function getListingBackHref(listing: ListingBackHrefSource | null | undefined): string {
  const cityAreaHref = srpHrefFromArea(findCityArea(listing?.areas));
  if (cityAreaHref) return withListingSrpSlug(cityAreaHref, listing);

  const addressHref = srpHrefFromResourceId(cityResourceIdFromAddress(listing?.address));
  return addressHref ? withListingSrpSlug(addressHref, listing) : DEFAULT_HDP_BACK_HREF;
}
